/**
 * The global recent-changes view, straight from CFG_audit.
 *
 * Exists because the second question after "who last touched this" is always
 * "what changed this week". Filterable by entity, which is the registry's
 * `audit_entity` for config, and `<region>.access` for changes to people's
 * access in a region the caller manages.
 *
 * Admins only (the API refuses everyone else). A general user still has each
 * row's own history in its edit panel.
 *
 * Scoped to the active region, like the sidebar: switching region switches
 * the feed. The feed is remounted per region so a type filter chosen in one
 * region never leaks into another, where it would name nothing.
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAudit, useSchema } from '../api/hooks'
import { useRegion } from '../region'
import type { AuditEntry, ConfigTypeSchema } from '../api/types'
import { formatTimestamp, shortenUpn } from '../lib/format'
import { CellValue } from '../fields/CellValue'
import { Banner, EmptyState, ErrorBanner, Spinner } from '../components/feedback'

function verb(entry: AuditEntry): { label: string; tone: string } {
  if (!entry.old_value) return { label: 'Created', tone: 'badge--on' }
  if (!entry.new_value) return { label: 'Deleted', tone: 'badge--danger' }
  return { label: 'Changed', tone: 'badge--info' }
}

const isAccess = (entity: string) => entity.endsWith('.access')

/** Access entries hold `{ role_key, grants: { area: canWrite } }`. */
const ACCESS_LABELS: Record<string, string> = { role_key: 'Role', grants: 'Screens' }

function formatAccessValue(name: string, value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (name !== 'grants' || typeof value !== 'object') return String(value)
  const entries = Object.entries(value as Record<string, boolean>)
  if (entries.length === 0) return 'none'
  return entries.map(([area, canWrite]) => `${area} (${canWrite ? 'edit' : 'view'})`).join(', ')
}

function changedFields(schema: ConfigTypeSchema | undefined, entry: AuditEntry) {
  const before = entry.old_value ?? {}
  const after = entry.new_value ?? {}
  const names = new Set([...Object.keys(before), ...Object.keys(after)])
  const changes: { name: string; label: string; from: unknown; to: unknown }[] = []

  for (const name of names) {
    if (name === 'updated_at' || name === 'updated_by') continue
    if (name === 'granted_at' || name === 'granted_by') continue
    if (JSON.stringify(before[name]) === JSON.stringify(after[name])) continue
    const field = schema?.fields.find((f) => f.name === name)
    const label = isAccess(entry.entity) ? (ACCESS_LABELS[name] ?? name) : (field?.label ?? name)
    changes.push({ name, label, from: before[name], to: after[name] })
  }
  return changes
}

export function AuditPage() {
  const { data } = useSchema()
  const { region } = useRegion()
  if (data && !data.user.can_manage_access) {
    return (
      <div className="page">
        <Banner tone="info" title="Recent changes is for admins">
          Open any row to see its own history in the edit panel.
        </Banner>
      </div>
    )
  }
  return <AuditFeed key={region} region={region} />
}

function AuditFeed({ region }: { region: string }) {
  const schemaQuery = useSchema()
  const [entity, setEntity] = useState('')
  const audit = useAudit({ region: region || undefined, entity: entity || undefined, page_size: 200 })

  const schemaByEntity = useMemo(() => {
    const map: Record<string, ConfigTypeSchema> = {}
    for (const type of schemaQuery.data?.config_types ?? []) map[type.audit_entity] = type
    return map
  }, [schemaQuery.data])

  const entities = (schemaQuery.data?.config_types ?? []).filter((t) => t.region === region)

  // Access changes are visible for the regions the caller manages.
  const user = schemaQuery.data?.user
  const regions = schemaQuery.data?.regions ?? []
  const managesRegion = user?.is_global_admin || (user?.is_admin && user.region === region)
  const managed = managesRegion ? regions.filter((r) => r.key === region) : []
  const regionLabel = (entity: string) =>
    regions.find((r) => `${r.key}.access` === entity)?.label ?? entity.split('.')[0]

  return (
    <div className="page">
      <header className="page__header">
        <h1 className="page__title">
          Recent changes — {regions.find((r) => r.key === region)?.label ?? region}
        </h1>
        <p className="page__description">
          Every mutation made through this UI, appended in the same transaction as the
          change itself. Nothing here can be edited or removed — that is the point of it.
        </p>
      </header>

      <ErrorBanner error={audit.error} context="Could not load the audit trail." />

      <div className="card">
        <div className="toolbar">
          <label className="visually-hidden" htmlFor="audit-entity">
            Filter by type
          </label>
          <select
            id="audit-entity"
            className="select select--filter"
            value={entity}
            onChange={(event) => setEntity(event.target.value)}
          >
            <option value="">Everything in this region</option>
            {entities.map((type) => (
              <option key={type.audit_entity} value={type.audit_entity}>
                {type.label}
              </option>
            ))}
            {managed.map((r) => (
              <option key={r.key} value={`${r.key}.access`}>
                Access — {r.label}
              </option>
            ))}
          </select>
          <span className="toolbar__spacer" />
          <span className="toolbar__count">
            {audit.isFetching ? 'Loading…' : `${audit.data?.total ?? 0} entries`}
          </span>
        </div>

        {audit.isLoading ? (
          <Spinner />
        ) : (audit.data?.entries.length ?? 0) === 0 ? (
          <EmptyState title="No changes recorded yet">
            Changes appear here as soon as someone saves one.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Who</th>
                  <th scope="col">What</th>
                  <th scope="col">Row</th>
                  <th scope="col">Change</th>
                </tr>
              </thead>
              <tbody>
                {audit.data?.entries.map((entry) => {
                  const schema = schemaByEntity[entry.entity]
                  const action = verb(entry)
                  const changes = changedFields(schema, entry)
                  return (
                    <tr key={entry.id} style={{ cursor: 'default' }}>
                      <td className="table__cell--muted">{formatTimestamp(entry.changed_at)}</td>
                      <td className="table__cell--muted">{shortenUpn(entry.changed_by)}</td>
                      <td>
                        <span className={`badge ${action.tone}`}>{action.label}</span>{' '}
                        {schema?.label ??
                          (isAccess(entry.entity) ? `Access — ${regionLabel(entry.entity)}` : entry.entity)}
                      </td>
                      <td className="table__cell--key">
                        {schema ? (
                          <Link to={`/config/${schema.key}?row=${encodeURIComponent(entry.entity_key)}`}>
                            {entry.entity_key}
                          </Link>
                        ) : (
                          entry.entity_key
                        )}
                      </td>
                      <td>
                        {changes.length === 0 ? (
                          <span className="table__cell--muted">—</span>
                        ) : (
                          <dl className="diff">
                            {changes.map((change) => {
                              const field = schema?.fields.find((f) => f.name === change.name)
                              return (
                                <div key={change.name} style={{ display: 'contents' }}>
                                  <dt className="diff__field">{change.label}</dt>
                                  <dd className="diff__values">
                                    <span className="diff__old">
                                      {isAccess(entry.entity) ? (
                                        formatAccessValue(change.name, change.from)
                                      ) : (
                                        <CellValue field={field} value={change.from} />
                                      )}
                                    </span>
                                    <span className="diff__arrow">→</span>
                                    <span className="diff__new">
                                      {isAccess(entry.entity) ? (
                                        formatAccessValue(change.name, change.to)
                                      ) : (
                                        <CellValue field={field} value={change.to} />
                                      )}
                                    </span>
                                  </dd>
                                </div>
                              )
                            })}
                          </dl>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
